import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Loader2, Building2, Upload, Image as ImageIcon } from 'lucide-react';
import logger from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { company_name: '', email: '', phone: '', sector: 'general', description: '', website: '', contact_name: '', products_services: '', logo_url: '' };

const SECTORS = [
  { value: 'construction', label: 'Construction' },
  { value: 'materiaux', label: 'Matériaux' },
  { value: 'technologie', label: 'Technologie' },
  { value: 'services', label: 'Services' },
  { value: 'general', label: 'Général' },
];

export default function ManageExposants() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/exhibitors-admin/all`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_logo', 'true');
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm({ ...form, logo_url: `${BACKEND_URL}${res.data.url}` });
      toast.success('Logo uploadé');
    } catch (err) { logger.error('Logo upload failed:', err); toast.error('Erreur lors de l\'upload'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const openMediaPicker = async () => {
    try {
      const res = await axios.get(`${API}/gallery/all`, { headers });
      setGallery(res.data);
    } catch (e) { logger.error('Gallery fetch failed:', e); toast.error('Bibliothèque inaccessible'); }
    setMediaOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_name || !form.email) { toast.error('Nom et email requis'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/exhibitors/${editId}`, form, { headers });
        toast.success('Exposant mis à jour');
      } else {
        await axios.post(`${API}/exhibitors`, form, { headers });
        toast.success('Exposant ajouté');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditId(null);
      fetchItems();
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      company_name: item.company_name, email: item.email || '', phone: item.phone || '',
      sector: item.sector || 'general', description: item.description || '', website: item.website || '',
      contact_name: item.contact_name || '', products_services: item.products_services || '',
      logo_url: item.logo_url || ''
    });
    setEditId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet exposant ?')) return;
    try {
      await axios.delete(`${API}/exhibitors/${id}`, { headers });
      toast.success('Supprimé');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const openAdd = () => { setForm(emptyForm); setEditId(null); setDialogOpen(true); };
  const logoSrc = (url) => url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;

  return (
    <div data-testid="manage-exposants">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Exposants</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} exposant(s)</p>
        </div>
        <Button onClick={openAdd} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-exposant-btn">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Logo</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Secteur</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="w-20">Statut</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.logo_url ? (
                      <img src={logoSrc(item.logo_url)} alt="" className="w-10 h-10 rounded object-contain bg-[#F7F9FC]" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[#F0F2F5] flex items-center justify-center"><Building2 className="w-4 h-4 text-[#94A3B8]" /></div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{item.company_name}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 rounded-full bg-[#F0F2F5] capitalize">{item.sector}</span></TableCell>
                  <TableCell className="text-[#6B7C93] text-sm">{item.email}</TableCell>
                  <TableCell className="text-sm">{item.contact_name || '-'}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {item.is_active !== false ? 'Actif' : 'Inactif'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-exposant-${item.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-exposant-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun exposant</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Modifier' : 'Ajouter'} un exposant</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-2">
            {/* Logo upload */}
            <div>
              <Label className="text-sm font-semibold">Logo</Label>
              <div className="flex items-center gap-3 mt-1.5">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="w-16 h-16 rounded-lg object-contain bg-[#F7F9FC] border border-[#E1E8F0]" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-[#F0F2F5] border border-dashed border-[#D1D5DB] flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-[#94A3B8]" />
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-1.5">
                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A5F] text-white text-xs font-medium rounded-lg cursor-pointer hover:bg-[#152A45] transition-colors">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Upload...' : 'Uploader'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} data-testid="exposant-logo-upload" />
                    </label>
                    <Button type="button" variant="outline" size="sm" onClick={openMediaPicker} className="text-xs h-auto py-1.5" data-testid="exposant-media-picker">
                      <ImageIcon className="w-3.5 h-3.5 mr-1" />Bibliothèque
                    </Button>
                  </div>
                  {form.logo_url && (
                    <button onClick={() => setForm({ ...form, logo_url: '' })} className="text-xs text-red-500 hover:text-red-700 text-left">Supprimer le logo</button>
                  )}
                </div>
              </div>
            </div>
            <div><Label>Entreprise *</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="exposant-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="exposant-email-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Contact</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            </div>
            <div>
              <Label>Secteur</Label>
              <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
            <div><Label>Site web</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Produits/Services</Label><Textarea value={form.products_services} onChange={(e) => setForm({ ...form, products_services: e.target.value })} rows={2} /></div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="exposant-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editId ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Picker */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[70vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Sélectionner un logo depuis la bibliothèque</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto mt-2">
            {gallery.length === 0 ? (
              <p className="text-center py-8 text-[#94A3B8]">Aucune image dans la bibliothèque</p>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {gallery.map(img => (
                  <button
                    key={img.id}
                    onClick={() => { setForm({ ...form, logo_url: `${BACKEND_URL}${img.url}` }); setMediaOpen(false); }}
                    className="rounded-lg overflow-hidden border-2 border-transparent hover:border-[#F5A623] transition-colors"
                    data-testid={`pick-logo-${img.id}`}
                  >
                    <img src={`${BACKEND_URL}${img.url}`} alt={img.title} className="w-full h-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
